import {Grid, Typography, makeStyles} from '@material-ui/core';
import {getEvaluatePayload} from 'Quoting/api/apiHelpers';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {setCompensationError, setCompensationValue, setIsCompensationUpdated} from 'Quoting/ducks/compensation';
import {updateBackdropState} from 'Quoting/ducks/createQuote';
import {evaluateDecisionTable} from 'Quoting/ducks/quoteActions';
import {CompensationValue, DecisionHeapedTable, EvaluateLevelPayload} from 'Quoting/models/DTOs/CompensationPayload';
import {A360Plan} from 'Quoting/models/Quoting';
import {VALUE_NOT_AVAILABLE, isDefined, isNumericValue} from 'Quoting/utils/utilities';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {QuoteFormElement} from '../../shared/QuoteFormElement';
import {AvailablePlans} from '../Plan/products/types';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
  },
  label: {
    color: '#979797',
    fontSize: '0.875rem',
    lineHeight: '1.05rem',
  },
  noRecordParent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '25vh',
    textAlign: 'center',
    padding: '15px 5px',
    width: '100%',
  },
  alignRecordCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: 'auto',
  },
  typeField: {
    display: 'flex',
    alignItems: 'center',
  },
  rootCompensation: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: '#444444',
    padding: theme.spacing(2),
    overflow: 'hidden',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    background: '#f5f5f5',
  },
  gridHeader: {
    fontWeight: 'bold',
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  gridCell: {
    textAlign: 'center',
    padding: theme.spacing(1),
    borderBottom: '1px solid #ccc',
  },
  heapedCompensationTable: {
    borderBottom: '1px solid #ccc',
    padding: '1rem 0',
    textAlign: 'center',
  },
  overrideCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: '#474747',
    },
  },
}));

interface HeapedResponse {
  levelEqu: string;
  heapedValues: DecisionHeapedTable[];
}

const LEVEL_COMPENSATION_STEP = 0.0001;
const MAX_VALUE_COMPENSATION = 22.5;
const MIN_VALUE_COMPENSATION = 0;

const RATE = '20';
const GVCI4_CO_RATE = '15';
const GVCI4_NJ_ATTAINEDAGE_RATE = '3';

export const getMaxCompValError = (val: string | number) =>
  `The value must be between ${MIN_VALUE_COMPENSATION}% and ${val}% with step of ${LEVEL_COMPENSATION_STEP}%`;

const fetchMappingOptions = (values: DecisionHeapedTable[]) => {
  return values.map((val, index) => {
    const displayVal = val.heapedCommission.split(' / ');

    return {
      displayValue: `${checkIfInteger(displayVal[0])}% / ${checkIfInteger(displayVal[1])}%`,
      code: val.heapedCommission,
      orderNo: index,
      levelEquivalent: val.levelEquivalent,
      heapedIssueAge: val.heapedIssueAge,
    };
  });
};

export const getRate = (plan: AvailablePlans, state: string, heapedIssueAge: boolean, maxCommissionValue: string) => {
  let equ = '0';
  if (plan === A360Plan.GVCI4) {
    if (state === 'CO' || (state === 'NJ' && heapedIssueAge)) equ = GVCI4_CO_RATE;
    else if (state === 'NJ' && !heapedIssueAge) equ = GVCI4_NJ_ATTAINEDAGE_RATE;
    else equ = Math.min(Number(maxCommissionValue), 20).toString();
  } else equ = Math.min(Number(maxCommissionValue), 20).toString();
  return equ;
};

export const fetchHeapedValues = (
  values: DecisionHeapedTable[],
  plan: AvailablePlans,
  state: string,
  heapedIssueAge: boolean,
  maxCommissionValue: string
) => {
  const equ = getRate(plan, state, heapedIssueAge, maxCommissionValue);
  const res =
    (values.filter(val => Number(val.levelEquivalent) === Number(equ)).length &&
      values.filter(val => Number(val.levelEquivalent) === Number(equ))) ||
    values
      .filter(val => Number(val.levelEquivalent) < Number(equ))
      .sort((a, b) => Number(a.levelEquivalent) - Number(b.levelEquivalent));

  return fetchMappingOptions(res);
};

export const CompensationTypeForm = ({
  disabled,
  isConfirmDisabled,
}: {
  disabled: boolean;
  isConfirmDisabled: (isDisabled: boolean) => void;
}) => {
  const coverageIndex = 0;
  const issueAge = 'IssueAge';
  const heaped = 'Heaped';
  const level = 'Level';

  const classes = useStyles();
  const dispatch = useDispatch();
  const {getQuoteFieldValue} = useQuoteField();

  const {compensationValue, compensationError} = useSelector((state: ReducerState) => state.compensation);
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const {decisionTableLevelResponse, decisionTableHeapedResponse} = useSelector(
    (state: ReducerState) => state.quoteActions
  );

  const [levelEquivalentVal, setLevelEquivalentVal] = useState<number>(0);
  const [isAllowedIssueAge, setIsAllowedIssueAge] = useState<boolean>(false);

  const IssueAgeHeapedError = 'Rate schedule only available for Rate Structure = Issue Age';
  const getExceededValueError = (maxCommissionLevel: number) =>
    `Rate may not exceed ${maxCommissionLevel}% for ${getValueFromQuote('riskStateCd')}`;

  const requiredError = 'Rate is required';

  const getValueFromQuote = (path: string) => {
    return getQuoteFieldValue<string>(path) ?? '';
  };

  const selectedPlanCd =
    getQuoteFieldValue<string>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`) ??
    VALUE_NOT_AVAILABLE;
  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';

  const handleCompensationTypeChange = async (event: React.ChangeEvent<{value: unknown}>) => {
    let latestDecisionTableResponse: string | undefined;
    let decisionTableHeapedResponseReturned: DecisionHeapedTable[] = [];
    if (decisionTableLevelResponse.length === 0) {
      const evaluatePayload = {
        packageCd: getValueFromQuote('benefitPackagingDetail.packageCd'),
        riskStateCd: getValueFromQuote('riskStateCd'),
        planCd: getValueFromQuote(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}].planCd`),
        rateRequestDate: getValueFromQuote('rateRequestDate'),
        termEffectiveDate: getValueFromQuote('termDetails.termEffectiveDate'),
        techFeePct: getQuoteFieldValue<number>('techPartners[0].techFeePct') ?? 0,
        coverageBasisCd: getValueFromQuote(
          `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[${coverageIndex}].coverageBasisCd`
        ),
      };
      dispatch(updateBackdropState(true));
      const result = await new Promise<HeapedResponse>(resolve => {
        dispatch(
          evaluateDecisionTable(
            getEvaluatePayload(evaluatePayload, level) as EvaluateLevelPayload,
            getEvaluatePayload(evaluatePayload, heaped),
            getValueFromQuote('productCd'),
            resolve
          )
        );
      });
      latestDecisionTableResponse = result.levelEqu;
      decisionTableHeapedResponseReturned = result.heapedValues;
    }
    if (latestDecisionTableResponse || decisionTableLevelResponse.length > 0) {
      const heapedValue = fetchHeapedValues(
        decisionTableHeapedResponseReturned?.length ? decisionTableHeapedResponseReturned : decisionTableHeapedResponse,
        selectedPlanCd as AvailablePlans,
        situsStateCd,
        getQuoteFieldValue('rateStructureCd') === issueAge,
        isDefined(decisionTableLevelResponse[0])
          ? decisionTableLevelResponse[0].maxCommissionLevel.toString()
          : latestDecisionTableResponse
      );

      dispatch(
        setCompensationValue({
          type: event.target.value as string,
          rate:
            event.target.value === heaped
              ? (selectedPlanCd === A360Plan.GCI5 || selectedPlanCd === A360Plan.GVCI4) && heapedValue.length > 1
                ? heapedValue.find(obj => obj.heapedIssueAge === (getQuoteFieldValue('rateStructureCd') === issueAge))
                    ?.code
                : heapedValue[heapedValue.length - 1]?.code
              : selectedPlanCd === A360Plan.GVCI4
              ? getRate(
                  selectedPlanCd,
                  situsStateCd,
                  getQuoteFieldValue('rateStructureCd') === issueAge,
                  latestDecisionTableResponse ?? decisionTableLevelResponse[0]?.maxCommissionLevel.toString()
                )
              : Math.min(
                  Number(latestDecisionTableResponse ?? decisionTableLevelResponse[0]?.maxCommissionLevel),
                  20
                ).toString(),
        })
      );
    }

    dispatch(updateBackdropState(false));
    dispatch(setIsCompensationUpdated(true));
    isConfirmDisabled(false);
  };

  const handleRateValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regexPattern = /^\d+(\.\d{0,4})?$/;
    const compensationHasError =
      event.target.value?.trim() === '' ||
      !isNumericValue(event.target.value) ||
      Number(event.target.value) > MAX_VALUE_COMPENSATION ||
      Number(event.target.value) < MIN_VALUE_COMPENSATION ||
      Number(event.target.value) > decisionTableLevelResponse[0]?.maxCommissionLevel;

    if (compensationHasError) {
      const compensationError = getCompensationError({rate: event.target.value, type: level});
      dispatch(setCompensationError(compensationError));
      isConfirmDisabled(true);
    } else {
      dispatch(setCompensationError(undefined));
      isConfirmDisabled(false);
    }

    if (regexPattern.test(event.target.value) || event.target.value == '') {
      dispatch(setCompensationValue({...compensationValue, rate: event.target.value}));
    }
    dispatch(setIsCompensationUpdated(true));
  };

  const getCompensationError = ({rate, type}: CompensationValue) => {
    if (type !== '' && rate === '') {
      return requiredError;
    }
    if (
      type !== '' &&
      (Number(rate) > MAX_VALUE_COMPENSATION || Number(rate) < MIN_VALUE_COMPENSATION || !isNumericValue(rate))
    ) {
      return getMaxCompValError(MAX_VALUE_COMPENSATION);
    }

    return (
      Number(rate) > decisionTableLevelResponse[0]?.maxCommissionLevel &&
      getExceededValueError(decisionTableLevelResponse[0]?.maxCommissionLevel)
    );
  };

  const handleHeapedRateChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const levelEquivalentVal = Number(
      fetchHeapedValues(
        decisionTableHeapedResponse,
        selectedPlanCd as AvailablePlans,
        situsStateCd,
        getQuoteFieldValue('rateStructureCd') === issueAge,
        decisionTableLevelResponse[0]?.maxCommissionLevel.toString()
      ).find(val => val.code === event.target.value)?.levelEquivalent ?? '0'
    );

    const isAllowedIssueAgeVal =
      fetchHeapedValues(
        decisionTableHeapedResponse,
        selectedPlanCd as AvailablePlans,
        situsStateCd,
        getQuoteFieldValue('rateStructureCd') === issueAge,
        decisionTableLevelResponse[0]?.maxCommissionLevel.toString()
      ).find(val => val.code === event.target.value)?.heapedIssueAge ?? false;

    const compensationHasError =
      (event.target.value as string)?.trim() === '' ||
      levelEquivalentVal > MAX_VALUE_COMPENSATION ||
      levelEquivalentVal > decisionTableLevelResponse[0]?.maxCommissionLevel ||
      (getQuoteFieldValue('rateStructureCd') !== issueAge && isAllowedIssueAgeVal);

    if (compensationHasError) {
      dispatch(
        setCompensationError(
          decisionTableLevelResponse &&
            (((event.target.value as string)?.trim() === '' && requiredError) ||
              (levelEquivalentVal > MAX_VALUE_COMPENSATION && getMaxCompValError(MAX_VALUE_COMPENSATION)) ||
              (getQuoteFieldValue('rateStructureCd') !== issueAge && isAllowedIssueAgeVal && IssueAgeHeapedError) ||
              (levelEquivalentVal > decisionTableLevelResponse[0]?.maxCommissionLevel &&
                getExceededValueError(decisionTableLevelResponse[0]?.maxCommissionLevel)))
        )
      );
      isConfirmDisabled(true);
    } else {
      dispatch(setCompensationError(undefined));
      isConfirmDisabled(false);
    }

    setLevelEquivalentVal(levelEquivalentVal);
    setIsAllowedIssueAge(isAllowedIssueAgeVal);
    dispatch(setCompensationValue({...compensationValue, rate: event.target.value as string}));
    dispatch(setIsCompensationUpdated(true));
  };

  const compensationTypeOptions = [
    {displayValue: level, code: level, orderNo: 0},
    {displayValue: heaped, code: heaped, orderNo: 1},
  ];

  const columns = [{label: 'Start Year'}, {label: 'End Year'}, {label: 'Rate'}];

  return (
    <div className={classes.root}>
      <Grid container spacing={2} style={{marginBottom: 16}}>
        <Grid item xs={12} sm={4} style={{marginTop: '15px'}}>
          <QuoteFormElement
            elementType="Select"
            id="type"
            label="Type"
            value={compensationValue.type}
            onChange={handleCompensationTypeChange}
            options={compensationTypeOptions}
            disabled={disabled}
            required
          />
        </Grid>
      </Grid>
      {compensationValue.type === level && (
        <Grid container spacing={2} style={{marginBottom: 16}}>
          <Grid item xs={12} sm={4}>
            <QuoteFormElement
              elementType="Text"
              id="rate"
              label="Rate %"
              type="number"
              value={
                selectedPlanCd === A360Plan.GVCI4
                  ? getRate(
                      selectedPlanCd,
                      situsStateCd,
                      getQuoteFieldValue('rateStructureCd') === issueAge,
                      decisionTableLevelResponse[0]?.maxCommissionLevel.toString()
                    )
                  : Math.min(decisionTableLevelResponse[0]?.maxCommissionLevel, 20)
              }
              onChange={handleRateValueChange}
              onKeyDown={e => {
                if (['e', 'E', '-', '+'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={true}
              error={
                compensationValue.rate?.trim() === '' ||
                !isNumericValue(compensationValue.rate) ||
                Number(compensationValue.rate) > MAX_VALUE_COMPENSATION ||
                Number(compensationValue.rate) < MIN_VALUE_COMPENSATION ||
                Number(compensationValue.rate) > decisionTableLevelResponse[0]?.maxCommissionLevel
              }
              helperText={decisionTableLevelResponse && compensationError ? compensationError : undefined}
              required
              inputProps={{
                step: LEVEL_COMPENSATION_STEP,
                min: MIN_VALUE_COMPENSATION,
                max: MAX_VALUE_COMPENSATION,
              }}
            />
          </Grid>
        </Grid>
      )}
      {compensationValue.type === heaped && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <QuoteFormElement
                elementType="Select"
                id="rate"
                label="Rate"
                required
                value={compensationValue.rate}
                disabled={true}
                options={fetchMappingOptions(decisionTableHeapedResponse)}
                onChange={handleHeapedRateChange}
                error={
                  compensationValue.rate?.trim() === '' ||
                  levelEquivalentVal > MAX_VALUE_COMPENSATION ||
                  (getQuoteFieldValue('rateStructureCd') !== issueAge && isAllowedIssueAge)
                }
                helperText={
                  decisionTableLevelResponse &&
                  ((compensationValue.rate?.trim() === '' && requiredError) ||
                    (levelEquivalentVal > MAX_VALUE_COMPENSATION && getMaxCompValError(MAX_VALUE_COMPENSATION)) ||
                    (getQuoteFieldValue('rateStructureCd') !== issueAge && isAllowedIssueAge && IssueAgeHeapedError))
                  //    ||
                  // (levelEquivalentVal > decisionTableLevelResponse[0]?.maxCommissionLevel &&
                  //   getExceededValueError(decisionTableLevelResponse[0]?.maxCommissionLevel))
                }
              />
            </Grid>
          </Grid>
          <div className={classes.rootCompensation}>
            <div className={classes.gridContainer}>
              {columns.map(column => (
                <Typography key={column.label} className={classes.gridHeader}>
                  {column.label}
                </Typography>
              ))}
            </div>
            <Grid container direction="column">
              <Grid container justifyContent="space-around" className={classes.heapedCompensationTable}>
                <Grid item xs={4}>
                  1
                </Grid>
                <Grid item xs={4}>
                  1
                </Grid>
                <Grid item xs={4}>
                  {renderCompensationRate(compensationValue?.rate?.split(' / ')?.[0])}
                </Grid>
              </Grid>

              <Grid container justifyContent="space-around" className={classes.heapedCompensationTable}>
                <Grid item xs={4}>
                  2
                </Grid>
                <Grid item xs={4}>
                  100
                </Grid>
                <Grid item xs={4}>
                  {renderCompensationRate(compensationValue?.rate?.split(' / ')?.[1])}
                </Grid>
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </div>
  );
};

const renderCompensationRate = (rate: string) => (!rate || rate?.trim() === '' ? '0%' : `${checkIfInteger(rate)}%`);

const checkIfInteger = (value: string) => (Number.isInteger(Number(value)) ? Number.parseInt(value) : value);
